/**
 * MathMessageBubble — message display for math agent chat.
 * Renders markdown with KaTeX math support ($...$ inline, $$...$$ block).
 * Fully independent from V7MessageBubble.
 */

import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { splitMathSegments, renderLatex, normalizeLatexDelimiters } from './katex-renderer';
import styles from './MathMessageBubble.module.css';
import 'katex/dist/katex.min.css';

// Render text with KaTeX math expressions
const renderMathText = (text) => {
    if (!text || typeof text !== 'string') return text;

    const segments = splitMathSegments(text);
    if (segments.length === 1 && segments[0].type === 'text') return text;

    return segments.map((seg, i) => {
        if (seg.type === 'text') return seg.content;
        const html = renderLatex(seg.content, seg.type === 'block');
        return (
            <span
                key={i}
                className={seg.type === 'block' ? styles.mathBlock : styles.mathInline}
                dangerouslySetInnerHTML={{ __html: html }}
            />
        );
    });
};

// Process children: apply math rendering to string segments only
const processChildren = (children) => {
    if (typeof children === 'string') return renderMathText(children);
    if (Array.isArray(children)) {
        return children.flatMap((child) => {
            if (typeof child === 'string') {
                const result = renderMathText(child);
                return Array.isArray(result) ? result : [result];
            }
            return [child];
        });
    }
    return children;
};

// Custom markdown components with math rendering
const markdownComponents = {
    p: ({ children }) => {
        const processed = processChildren(children);
        return <p>{processed}</p>;
    },
    li: ({ children }) => {
        const processed = processChildren(children);
        return <li>{processed}</li>;
    },
    code: ({ inline, children, ...props }) => {
        if (inline) {
            return <code className={styles.inlineCode} {...props}>{children}</code>;
        }
        return <pre className={styles.codeBlock}><code {...props}>{children}</code></pre>;
    },
    strong: ({ children }) => {
        const processed = processChildren(children);
        return <strong>{processed}</strong>;
    },
    em: ({ children }) => {
        const processed = processChildren(children);
        return <em>{processed}</em>;
    },
};

const MathMessageBubble = ({ message }) => {
    const isUser = message.role === 'user' || message.isUser;
    // Normalize \(...\) → $...$ and \[...\] → $$...$$ before ReactMarkdown
    // so backslashes aren't stripped by markdown escape processing
    const content = useMemo(() => {
        const raw = message.content || '';
        return isUser ? raw : normalizeLatexDelimiters(raw);
    }, [message.content, isUser]);

    // Show tools used (if any)
    const toolsUsed = useMemo(() => {
        if (!message.tools_used || message.tools_used.length === 0) return null;
        return message.tools_used;
    }, [message.tools_used]);

    return (
        <div className={`${styles.bubble} ${isUser ? styles.userBubble : styles.assistantBubble}`}>
            {!isUser && toolsUsed && (
                <div className={styles.toolsBar}>
                    {toolsUsed.map((tool, i) => (
                        <span key={i} className={styles.toolChip}>{tool}</span>
                    ))}
                </div>
            )}
            <div className={styles.content}>
                {isUser ? (
                    <p>{content}</p>
                ) : (
                    <ReactMarkdown components={markdownComponents}>
                        {content}
                    </ReactMarkdown>
                )}
            </div>
        </div>
    );
};

export default MathMessageBubble;
